import { useState, useEffect } from 'react';
import { createPublicClient, http } from 'viem';
import { mainnet } from 'viem/chains';

// Create a public client for Ethereum mainnet ENS resolution
const mainnetClient = createPublicClient({
  chain: mainnet,
  transport: http('https://eth.llamarpc.com'),
});

/**
 * Hook to fetch ENS domain name for a wallet address on Ethereum
 * @param address - Wallet address to lookup
 * @returns ensName (e.g., "vitalik.eth") or null if not found
 */
export function useENSName(address: string | undefined): {
  ensName: string | null;
  isLoading: boolean;
  error: string | null;
} {
  const [ensName, setEnsName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!address) {
      setEnsName(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Validate address format
    if (!address.match(/^0x[a-fA-F0-9]{40}$/)) {
      setEnsName(null);
      setIsLoading(false);
      return;
    }

    const fetchENSName = async () => {
      setIsLoading(true);
      setError(null);

      try {
        console.log('[ENS] Fetching domain for address:', address);
        
        const name = await mainnetClient.getEnsName({
          address: address as `0x${string}`,
        });
        
        if (name) {
          console.log('[ENS] Found domain:', name);
          setEnsName(name);
        } else {
          console.log('[ENS] No domain found for address:', address);
          setEnsName(null);
        }
      } catch (err) {
        console.error('[ENS] Error fetching domain:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        setEnsName(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchENSName();
  }, [address]);

  return { ensName, isLoading, error };
}

